<template>
  <div class="content">
    <h2 class="text-2xl font-bold mb-4">How Is Spin the Names Funded?</h2>

    <div class="mb-6">
      <p>Spin the Names is free to use, and keeping it that way requires a way to cover costs. The current model
        is advertising: non-intrusive ads displayed on the site generate the revenue that pays for hosting,
        infrastructure, and ongoing development. No subscriptions required, no features locked behind a paywall.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">What does the revenue actually go toward?</h3>
      <ul class="list-disc ml-6 mt-2">
        <li><strong>Hosting and infrastructure</strong> — Firebase, CDN, database, and storage costs scale
          with usage. More users means higher costs.</li>
        <li><strong>Development time</strong> — Building and maintaining software is time-intensive.
          Ad revenue makes it sustainable to keep working on improvements.</li>
        <li><strong>Bug fixes and reliability</strong> — Keeping the app working correctly across browsers,
          devices, and operating systems requires ongoing attention.</li>
        <li><strong>Multilingual support</strong> — Maintaining translations across English, Spanish, French,
          German, and Swedish adds complexity that takes time to manage well.</li>
      </ul>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Will there always be a free version?</h3>
      <p>Yes. The core tool — adding names, spinning the wheel, getting a fair random result — will always be
        free. That's a firm commitment, not a marketing line. Teachers shouldn't have to pay to run a fair
        classroom, and anyone running a giveaway shouldn't need a subscription to pick a winner transparently.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Is a premium tier coming?</h3>
      <p>Yes, eventually. The plan is to offer optional paid features for users who want more — things like
        ad-free browsing, additional saved wheels, advanced customization, export options, and analytics. These
        would be genuine extras, not features removed from the free tier to create artificial pressure to upgrade.</p>
      <p class="mt-2">Premium features will be announced when they're ready. In the meantime, everything
        currently available stays available.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Can I support the project without buying anything?</h3>
      <p>Absolutely. The most useful thing you can do is share Spin the Names with someone who'd find it helpful.
        Word of mouth from teachers, streamers, and event organizers is how most users find the app, and
        every new user makes it more sustainable.</p>
      <p class="mt-2">Leaving feedback, reporting bugs, and sending feature ideas also helps — it improves
        the product and guides development priorities without costing anything.</p>
    </div>

    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Commercial or high-volume use</h3>
      <p>If you're using Spin the Names at scale — large events, corporate environments, or as part of a
        commercial product — and you have specific needs around reliability, branding, or usage limits, reach
        out at <a href="mailto:ash@spinthenames.com" class="text-blue-500 hover:underline">ash@spinthenames.com</a>
        to discuss options.</p>
    </div>

    <p class="mt-8 text-sm"><a href="#top" class="text-blue-500 hover:underline">Back to top</a></p>
  </div>
</template>

<script setup>
import '@components/ui/faq/faqStyles.css';
</script>
